import { Inject, Injectable, OnDestroy } from '@angular/core'
import { Subject, takeUntil } from 'rxjs'
import { MapService } from 'src/app/core/services/map.service'
import {
  ExpressionSpecification,
  FillLayerSpecification,
  SymbolLayerSpecification,
} from 'maplibre-gl'
import { DataObservationsService } from '../services/data-observations.service'

import { emptyGeoJsonSource } from 'src/app/core/utils/source-utils'
import { AppStateService } from 'src/app/core/services/app-state.service'
import { NavigationStart, Router } from '@angular/router'
import { MapEventsService } from 'src/app/core/services/map-events.service'
import { MapSignsIcons, MapSupsIcons } from 'src/app/core/enums/map-icons.enum'
import { withSignTypeFilter } from '../utils/property-filter.utils'
// Import the generateH3Hexagons function from its module
import { generateH3Hexagons } from 'src/app/shared/utils/h3-utils'
import { FeatureCollection, Polygon } from 'geojson'

@Injectable()
export class LayerObservationsService implements OnDestroy {
  public isLayerVisible = false
  private cleanUp$ = new Subject<void>()
  private timestamp: Date | undefined
  private timespan: number | undefined
  public selectedFeatureId: string = '1'
  public onDestroy: boolean = false

  public layerId = this.layer
  public layerSubsSymbolsId = `${this.layer}-subs-symbols`
  public layerHexagonsId = `${this.layer}-hexagons`
  public sourceId = `${this.layerId}-src`
  public sourceHexagonsId = `${this.layerHexagonsId}-src`

  private observationsBaseFilter: ExpressionSpecification = ['all']
  private observationsSubTypesBaseFilter: ExpressionSpecification = [
    'all',
    ['!', ['has', 'point_count']],
    ['>', ['length', ['get', 'supplementalSign']], 0],
  ]

  private observationsLayerSpecification: SymbolLayerSpecification = {
    id: `${this.layer}`,
    source: this.sourceId,
    minzoom: this.minZoom,
    type: 'symbol',
    filter: this.observationsBaseFilter,
    layout: {
      'icon-rotation-alignment': 'map',
      'icon-ignore-placement': true,
      'icon-anchor': 'bottom',
      'icon-allow-overlap': true,
      'icon-rotate': ['get', 'heading'],
      'symbol-sort-key': [
        'case',
        this.getSelectedFeatureExpression(this.selectedFeatureId),
        1000000000000, // High value to bring selected feature to front
        0,
      ],
      'icon-image': [
        'case',
        // Speed limit signs with numeric values
        ['==', ['get', 'mainType'], MapSignsIcons.SPEED_LIMIT],
        [
          'case',
          ['==', ['get', 'displayType'], 'digital'],
          [
            'case',
            ['==', ['get', 'speedLimit'], null],
            ['concat', MapSignsIcons.SPEED_LIMIT, '_NULL_DIGITAL_SMALL'],
            ['concat', MapSignsIcons.SPEED_LIMIT, '_', ['get', 'speedLimit'], '_DIGITAL_SMALL'],
          ],
          ['concat', MapSignsIcons.SPEED_LIMIT, '_', ['get', 'speedLimit'], '_SMALL'],
        ],
        // Speed limit end signs
        ['==', ['get', 'mainType'], MapSignsIcons.SPEED_LIMIT_END],
        [
          'case',
          ['==', ['get', 'displayType'], 'digital'],
          ['concat', MapSignsIcons.SPEED_LIMIT_END, '_', ['get', 'speedLimit'], '_DIGITAL_SMALL'],
          ['concat', MapSignsIcons.SPEED_LIMIT_END, '_', ['get', 'speedLimit'], '_SMALL'],
        ],

        // Speed limit zone signs
        ['==', ['get', 'mainType'], MapSignsIcons.SPEED_LIMIT_ZONE],
        [
          'case',
          ['==', ['get', 'displayType'], 'digital'],
          ['concat', MapSignsIcons.SPEED_LIMIT_ZONE, '_', ['get', 'speedLimit'], '_DIGITAL_SMALL'],
          ['concat', MapSignsIcons.SPEED_LIMIT_ZONE, '_', ['get', 'speedLimit'], '_SMALL'],
        ],

        // Speed limit zone end signs
        ['==', ['get', 'mainType'], MapSignsIcons.SPEED_LIMIT_ZONE_END],
        [
          'case',
          ['==', ['get', 'displayType'], 'digital'],
          [
            'concat',
            MapSignsIcons.SPEED_LIMIT_ZONE_END,
            '_',
            ['get', 'speedLimit'],
            '_DIGITAL_SMALL',
          ],
          ['concat', MapSignsIcons.SPEED_LIMIT_ZONE_END, '_', ['get', 'speedLimit'], '_SMALL'],
        ],

        ['==', ['get', 'mainType'], MapSignsIcons.YIELD],
        [
          'case',
          ['==', ['get', 'displayType'], 'digital'],
          ['concat', MapSignsIcons.YIELD, '_DIGITAL_SMALL'],
          ['concat', MapSignsIcons.YIELD, '_SMALL'],
        ],

        // Simple icon mappings (no concatenation needed)
        ['==', ['get', 'mainType'], MapSignsIcons.ALL_RESTRICTIONS_END],
        [
          'case',
          ['==', ['get', 'displayType'], 'digital'],
          ['concat', MapSignsIcons.ALL_RESTRICTIONS_END, '_DIGITAL_SMALL'],
          ['concat', MapSignsIcons.ALL_RESTRICTIONS_END, '_SMALL'],
        ],

        ['==', ['get', 'mainType'], MapSignsIcons.CITY_START],
        ['concat', MapSignsIcons.CITY_START, '_SMALL'],

        ['==', ['get', 'mainType'], MapSignsIcons.CITY_END],
        ['concat', MapSignsIcons.CITY_END, '_SMALL'],

        ['==', ['get', 'mainType'], MapSignsIcons.HIGHWAY],
        ['concat', MapSignsIcons.HIGHWAY, '_SMALL'],

        ['==', ['get', 'mainType'], MapSignsIcons.HIGHWAY_END],
        ['concat', MapSignsIcons.HIGHWAY_END, '_SMALL'],

        ['==', ['get', 'mainType'], MapSignsIcons.MOTORWAY],
        ['concat', MapSignsIcons.MOTORWAY, '_SMALL'],

        ['==', ['get', 'mainType'], MapSignsIcons.MOTORWAY_END],
        ['concat', MapSignsIcons.MOTORWAY_END, '_SMALL'],

        ['==', ['get', 'mainType'], MapSignsIcons.TRAFFIC_CALMING],
        ['concat', MapSignsIcons.TRAFFIC_CALMING, '_SMALL'],

        ['==', ['get', 'mainType'], MapSignsIcons.TRAFFIC_CALMING_END],
        ['concat', MapSignsIcons.TRAFFIC_CALMING_END, '_SMALL'],

        // Default fallback for unknown types
        MapSignsIcons.UNKNOWN,
      ],
      'icon-size': ['case', this.getSelectedFeatureExpression(this.selectedFeatureId), 1, 0.9],
    },
    paint: {
      'icon-opacity': 0.4,
    },
  }

  private observationsSubTypesLayerSpecification: SymbolLayerSpecification = {
    id: this.layerSubsSymbolsId,
    source: this.sourceId,
    minzoom: this.minZoom,
    type: 'symbol',
    filter: this.observationsSubTypesBaseFilter,
    layout: {
      'icon-rotation-alignment': 'map',
      'icon-rotate': ['get', 'heading'],
      'icon-anchor': 'bottom',
      'icon-ignore-placement': false,
      'symbol-sort-key': [
        'case',
        this.getSelectedFeatureExpression(this.selectedFeatureId),
        1000000000000, // High value to bring selected feature to front
        0,
      ],
      'icon-allow-overlap': true,
      'icon-image': [
        'case',
        // Single supplemental sign - use different icon
        ['==', ['length', ['get', 'supplementalSign']], 1],
        ['concat', ['get', 'type', ['at', 0, ['get', 'supplementalSign']]], '_SMALL'],
        // Multiple supplemental signs - use existing unknown icon
        [
          'concat',
          MapSupsIcons.SUP_UNKNOWN,
          '_',
          ['to-string', ['length', ['get', 'supplementalSign']]],
          '_SMALL',
        ],
      ],
      'icon-size': ['case', this.getSelectedFeatureExpression(this.selectedFeatureId), 0.7, 0.6],
      'icon-offset': [0, 18],
    },
    paint: {
      'icon-opacity': 0.4,
    },
  }

  private hexagonLayerSpecification: FillLayerSpecification = {
    id: this.layerHexagonsId,
    minzoom: 14,
    type: 'fill',
    source: this.sourceHexagonsId, // reference the data source
    paint: {
      'fill-color': '#ffffff',
      'fill-opacity': 0.05,
      'fill-outline-color': '#ffffff',
    },
    layout: {
      visibility: 'none',
    },
  }

  constructor(
    private router: Router,
    private readonly mapService: MapService,
    private readonly mapEventsService: MapEventsService,
    private appState: AppStateService,
    @Inject(String) private layer: string,
    @Inject(Number) private minZoomAvailable: number,
    @Inject(Number) private maxZoomAvailable: number,
    @Inject(Number) private minZoom: number,
    private dataService: DataObservationsService,
  ) {
    this.selectedFeatureId = this.appState.getState().selectedFeatureId ?? ''

    if (mapService.isMapReady) {
      this.initializeLayer()
    } else {
      this.mapService.mapReadyChanged.subscribe(() => {
        this.initializeLayer()
      })
    }
    this.appState.stateChanged.pipe(takeUntil(this.cleanUp$)).subscribe((state) => {
      this.evaluateState()
    })

    // NgOnDestroy is not working on instances created by a factory, therefore we need this workaround
    this.router.events.pipe(takeUntil(this.cleanUp$)).forEach((event) => {
      if (event instanceof NavigationStart) {
        this.ngOnDestroy()
      }
    })
  }

  ngOnDestroy() {
    this.onDestroy = true
    this.cancelRequests()
    this.cleanUp$.next()
    this.mapEventsService.detachMapEvents(this)
    this.mapService.map.removeLayer(this.layerId)
    this.mapService.map.removeLayer(this.layerSubsSymbolsId)
    this.mapService.map.removeLayer(this.layerHexagonsId)
    this.mapService.map.removeSource(this.sourceId)
    this.mapService.map.removeSource(this.sourceHexagonsId)
  }

  private initializeLayer() {
    this.mapService.map.addSource(this.sourceId, { ...emptyGeoJsonSource, promoteId: 'id' })
    this.mapService.map.addSource(this.sourceHexagonsId, { ...emptyGeoJsonSource })
    this.mapService.map.addLayer(this.hexagonLayerSpecification)
    this.mapService.map.addLayer(this.observationsLayerSpecification)
    this.mapService.map.addLayer(this.observationsSubTypesLayerSpecification)

    this.mapEventsService.registerForMapEvents(this, true)
    this.evaluateState()
  }

  public async reload() {
    this.cancelRequests()
    if (
      this.mapService.isMapReady &&
      this.isLayerVisible &&
      this.mapService.map.getZoom() >= this.minZoom
    ) {
      const xyzTiles = this.mapService.getXyzTiles(this.maxZoomAvailable, this.minZoomAvailable)
      await this.dataService.loadObservationData(
        this.appState.isHistoryEnabled(),
        xyzTiles,
        this.appState.selectTimestamp(),
        this.mapService.getExpandedBounds(0.5),
      )
      this.updateLayer()
    }
  }

  public updateLayer() {
    if (this.mapService.isMapReady) {
      const observations = this.dataService.getObservations()
      this.changeSelectAndHoverStateOfSelectedFeature()
      this.mapService.map.removeFeatureState({
        source: this.sourceId,
        sourceLayer: this.sourceId,
      })
      this.mapService.setGeoJsonData(this.sourceId, observations)
    }
  }

  private evaluateState() {
    if (this.appState.selectLayers().some((layer) => layer === this.layer)) {
      this.setVisibility(true)
    } else {
      this.setVisibility(false)
    }

    // if layer is not visible stop all events
    if (this.isLayerVisible === false) return

    this.applySignTypeFilter()

    if (!this.timestamp) this.timestamp = this.appState.selectTimestamp()

    if (!this.dataService.isDataInitialized) {
      this.reload()
    }

    if (
      this.appState.hasLayerVisibilityChanged(this.layer) &&
      !this.appState.getState().historyEnabled
    ) {
      this.reload()
    }

    if (this.appState.hasMapBoundsChanged() && !this.appState.getState().historyEnabled) {
      this.reload()
    }

    if (this.appState.hasTimestampChanged()) {
      this.timestamp = this.appState.selectTimestamp()
      this.reload()
    }

    if (this.appState.hasSelectedFeatureIdChanged()) {
      this.selectedFeatureId = this.appState.getState().selectedFeatureId || ''
      this.updateLayer()
      this.changeSelectAndHoverStateOfSelectedFeature()
    }
  }

  private applySignTypeFilter() {
    const selectedSignTypes = this.appState.selectSignTypes()
    const propertyFilters = this.appState.selectPropertyFilters()

    if (this.mapService.map.getLayer(this.layerId)) {
      this.mapService.map.setFilter(
        this.layerId,
        withSignTypeFilter(this.observationsBaseFilter, selectedSignTypes, propertyFilters),
      )
    }

    if (this.mapService.map.getLayer(this.layerSubsSymbolsId)) {
      this.mapService.map.setFilter(
        this.layerSubsSymbolsId,
        withSignTypeFilter(this.observationsSubTypesBaseFilter, selectedSignTypes, propertyFilters),
      )
    }
  }

  setVisibility(isVisible: boolean): void {
    this.isLayerVisible = isVisible
    if (this.mapService.isMapReady) {
      if (this.mapService.getLayerVisibilty(this.layer) != this.isLayerVisible) {
        this.mapService.setLayerVisibility(this.layerId, isVisible)
        this.mapService.setLayerVisibility(this.layerSubsSymbolsId, isVisible)
        if (!isVisible) {
          this.cancelRequests()
        }
      }
    }
  }

  // Update your cancelRequests method to properly cancel HTTP requests
  private cancelRequests() {
    // Cancel requests in the data service
    this.dataService.cancelCurrentRequests()
  }
  private changeSelectAndHoverStateOfSelectedFeature() {
    if (this.onDestroy) return
    if (this.selectedFeatureId != undefined) {
      this.setHexagonSource(this.selectedFeatureId)

      this.mapService.map.setLayoutProperty(this.layerId, 'icon-size', [
        'case',
        this.getSelectedFeatureExpression(this.selectedFeatureId),
        1.2,
        0.9,
      ])
      this.mapService.map.setLayoutProperty(this.layerSubsSymbolsId, 'icon-size', [
        'case',
        this.getSelectedFeatureExpression(this.selectedFeatureId),
        0.7,
        0.6,
      ])
      this.mapService.map.setLayoutProperty(this.layerSubsSymbolsId, 'icon-size', [
        'case',
        this.getSelectedFeatureExpression(this.selectedFeatureId),
        0.7,
        0.6,
      ])

      this.mapService.map.setLayoutProperty(this.layerId, 'symbol-sort-key', [
        'case',
        this.getSelectedFeatureExpression(this.selectedFeatureId),
        1000000000000, // High value to bring selected feature to front
        0,
      ])
      this.mapService.map.setLayoutProperty(this.layerSubsSymbolsId, 'icon-offset', [0, 18])
      this.mapService.map.setLayoutProperty(this.layerSubsSymbolsId, 'text-offset', [
        'case',
        this.getSelectedFeatureExpression(this.selectedFeatureId),
        ['literal', [0, 1.75]], // Selected feature: text positioned above icon
        ['literal', [0, 1.6]], // Non-selected feature: text positioned on icon
      ])
    }
  }

  private setHexagonSource(selectedFeatureId: string) {
    let currentFeature = this.mapService.map
      .queryRenderedFeatures({ layers: [this.layer] })
      .filter((feature) => {
        return (
          String((feature.properties as any)?.id ?? '') === String(selectedFeatureId) ||
          String(feature.id ?? '') === String(selectedFeatureId)
        )
      })

    if (currentFeature[0]) {
      const [lng, lat] = (currentFeature[0]?.geometry as GeoJSON.Point).coordinates

      // Generate H3 hexagons with fixed resolution of 10
      const hexagons = generateH3Hexagons(lat, lng, {
        resolution: 10, // Fixed resolution
        kRingDistance: 1, // Adjust the number of rings as needed
      })

      const hexagonCollection: FeatureCollection<Polygon> = {
        type: 'FeatureCollection',
        features: hexagons,
      }

      // Update the hexagon source
      this.mapService.setGeoJsonData(this.sourceHexagonsId, hexagonCollection)

      // Make hexagon layer visible
      this.mapService.setLayerVisibility(this.layerHexagonsId, true)
    } else {
      // If no feature is selected, hide the hexagon layer
      this.mapService.setLayerVisibility(this.layerHexagonsId, false)
    }
  }

  private getSelectedFeatureExpression(selectedFeatureId: string): ExpressionSpecification {
    return ['any', ['==', ['to-string', ['get', 'id']], selectedFeatureId || '']]
  }
}
